import api from './api';
import { 
  Quotation, 
  QuotationResponse, 
  QuotationFilters, 
  CreateQuotationRequest,
  UpdateQuotationRequest,
  AutoItemsResponse
} from '../types/quotation';

export const quotationService = {
  // Get all quotations with filters
  getQuotations: async (filters: QuotationFilters = {}): Promise<QuotationResponse> => {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.enquiry_id) params.append('enquiry_id', filters.enquiry_id.toString());
    if (filters.is_active) params.append('is_active', filters.is_active);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/quotations?${params.toString()}`);
    return response.data;
  },

  // Get single quotation by ID
  getQuotation: async (id: number): Promise<Quotation> => {
    const response = await api.get(`/quotations/${id}`);
    return response.data;
  },

  // Get auto-generated items for enquiry
  getAutoItems: async (enquiryId: number): Promise<AutoItemsResponse> => {
    const response = await api.get(`/quotations/auto-items/${enquiryId}`);
    return response.data;
  },

  // Create new quotation
  createQuotation: async (data: CreateQuotationRequest): Promise<{ message: string; quotation: { id: number; quote_no: string } }> => {
    const response = await api.post('/quotations', data);
    return response.data;
  },

  // Clone quotation (create new version)
  cloneQuotation: async (id: number): Promise<{ message: string; quotation: { id: number; quote_no: string; version: number } }> => {
    const response = await api.post(`/quotations/${id}/clone`);
    return response.data;
  },

  // Update quotation
  updateQuotation: async (id: number, data: UpdateQuotationRequest): Promise<{ message: string }> => {
    const response = await api.put(`/quotations/${id}`, data);
    return response.data;
  },

  // Delete quotation (soft delete)
  deleteQuotation: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/quotations/${id}`);
    return response.data;
  },

  // Share quotation via WhatsApp
  shareQuotation: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/quotations/${id}/share`);
    return response.data;
  },

  // Helper function to calculate quotation totals
  calculateTotals: (details: CreateQuotationRequest['quotationDetails']) => {
    const sub_total = details.reduce((sum, detail) => sum + detail.amount, 0);
    const net_amount = sub_total; // No tax, net amount equals subtotal
    
    return {
      sub_total: parseFloat(sub_total.toFixed(2)),
      net_amount: parseFloat(net_amount.toFixed(2))
    };
  },

  // Helper function to format currency
  formatCurrency: (amount: number | null | undefined): string => {
    if (!amount) return '₹0.00';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },

  // Helper function to format quotation number
  formatQuoteNumber: (quote_no: string, version: number): string => {
    return version > 1 ? `${quote_no} (v${version})` : quote_no;
  },

  // Helper function to get status badge color
  getStatusColor: (is_active: string, is_cancelled: string | null): string => {
    if (is_cancelled === 'Y') return 'bg-red-100 text-red-800';
    if (is_active === 'Y') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  },

  // Helper function to get status text
  getStatusText: (is_active: string, is_cancelled: string | null): string => {
    if (is_cancelled === 'Y') return 'Cancelled';
    if (is_active === 'Y') return 'Active';
    return 'Inactive';
  }
};